import { useState, useMemo } from 'react';

export interface UseFormOptions<T> {
  initialValues: T;
  onSubmit: (values: T) => Promise<void> | void;
  validate?: (values: T) => Partial<Record<keyof T, string>>;
  requiredFields?: (keyof T)[];
}

export interface UseFormReturn<T> {
  values: T;
  errors: Partial<Record<keyof T, string>>;
  isSubmitting: boolean;
  isDirty: boolean;
  isValid: boolean;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>, fieldName: keyof T) => void;
  handleSubmit: (e: React.FormEvent) => void;
  /**
   * The error the last submit rejected with, or null (task 774).
   *
   * Exposed rather than rethrown: `handleSubmit` is wired straight to `onSubmit`
   * on six forms, and rejecting it would turn every one of their submits into
   * an unhandled rejection. Callers that care read this; callers that do not are
   * unaffected, which is what keeps the change additive.
   */
  submitError: unknown;
  reset: () => void;
  setFieldValue: (name: keyof T, value: any) => void;
  setFieldError: (name: keyof T, error: string) => void;
  setValues: (values: T) => void;
}

export function useForm<T extends Record<string, any>>({
  initialValues,
  onSubmit,
  validate,
  requiredFields,
}: UseFormOptions<T>): UseFormReturn<T> {
  const [values, setValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [isDirty, setIsDirty] = useState(false);

  // Run validation continuously to get current validation errors
  const validationErrors = useMemo(() => {
    if (!validate) return {};
    return validate(values);
  }, [values, validate]);

  // Check if all required fields have values AND no validation errors
  const isValid = useMemo(() => {
    // Check for validation errors (e.g., invalid JSON)
    if (Object.keys(validationErrors).length > 0) return false;

    // Check required fields
    if (!requiredFields || requiredFields.length === 0) return true;
    return requiredFields.every((field) => {
      const value = values[field];
      if (typeof value === 'string') return value.trim().length > 0;
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'boolean') return true; // Booleans are always valid
      return value !== null && value !== undefined && value !== '';
    });
  }, [values, requiredFields, validationErrors]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const fieldName = name as keyof T;

    // Handle different input types
    let fieldValue: any = value;
    if (type === 'number') {
      fieldValue = value === '' ? '' : Number(value);
    }

    const newValues = { ...values, [fieldName]: fieldValue };
    setValues(newValues as T);
    setIsDirty(true);

    // Run validation and update errors for this field
    if (validate) {
      const newValidationErrors = validate(newValues as T);
      setErrors((prev) => {
        const updated = { ...prev };
        if (newValidationErrors[fieldName]) {
          updated[fieldName] = newValidationErrors[fieldName];
        } else {
          delete updated[fieldName];
        }
        return updated;
      });
    } else if (errors[fieldName]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, fieldName: keyof T) => {
    const file = e.target.files?.[0];
    if (file) {
      setValues((prev) => ({ ...prev, [fieldName]: file }));
      setIsDirty(true);

      // Clear error for this field
      if (errors[fieldName]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[fieldName];
          return newErrors;
        });
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Run validation if provided
    if (validate) {
      const validationErrors = validate(values);
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }
    }

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await onSubmit(values);
      // Reset form on success
      setValues(initialValues);
      setErrors({});
    } catch (error) {
      console.error('Form submission error:', error);
      // Kept for callers that need to render what the server refused (task 774)
      // — previously this was the error's only trace anywhere in the app.
      setSubmitError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const reset = () => {
    setValues(initialValues);
    setErrors({});
    setIsDirty(false);
  };

  const setFieldValue = (name: keyof T, value: any) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    setIsDirty(true);
  };

  const setFieldError = (name: keyof T, error: string) => {
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  return {
    values,
    errors,
    isSubmitting,
    isDirty,
    isValid,
    handleChange,
    handleFileChange,
    handleSubmit,
    submitError,
    reset,
    setFieldValue,
    setFieldError,
    setValues,
  };
}
